<script setup>
import { ref, computed, onMounted, watch, provide } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRouter, useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';

import FieldPalette from '../components/FormBuilder/FieldPalette.vue';
import FieldCanvas from '../components/FormBuilder/FieldCanvas.vue';
import FieldProperties from '../components/FormBuilder/FieldProperties.vue';
import FormSettings from '../components/FormBuilder/FormSettings.vue';
import StepManager from '../components/FormBuilder/StepManager.vue';
import EmbedCodeModal from '../components/FormBuilder/EmbedCodeModal.vue';
import { BRAND_PRIMARY } from 'dashboard/constants/brandConstants';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { accountScopedRoute, accountId } = useAccount();

const uiFlags = useMapGetter('forms/getUIFlags');

const formId = computed(() => route.params.formId);
const isEditing = computed(() => !!formId.value);

// Form data
const formName = ref('Novo Formulario');
const formDescription = ref('');
const formType = ref('standard');
const formStatus = ref('draft');
const submitAction = ref('message');
const submitRedirectUrl = ref('');
const submitThankYouMessage = ref('Obrigado pelo envio!');
const submitInboxId = ref(null);
const conversionIdentifier = ref('');
const primaryColor = ref(BRAND_PRIMARY);
const formTheme = ref('light');
const showBranding = ref(true);
const alsoCreateConversation = ref(false);
const redirectRules = ref([]);
const exitRules = ref([]);
const embedToken = ref('');
const formSlug = ref('');
const showEmbedModal = ref(false);

// Fields
const fields = ref([]);
const selectedFieldIndex = ref(null);
// Track DB IDs loaded from backend (to detect deletions on save)
const originalFieldDbIds = ref([]);
const originalStepDbIds = ref([]);

// Steps (for multi_step)
const steps = ref([{ id: 'step_1', name: 'Etapa 1', fields: [] }]);

// Right panel tab
const rightTab = ref('field');

const selectedField = computed(() => {
 if (selectedFieldIndex.value === null) return null;
 return fields.value[selectedFieldIndex.value] || null;
});

const isSaving = computed(
 () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);

const statusBadgeClass = computed(() => {
 const map = {
 draft:
 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800',
 published:
 'bg-green-50 dark:bg-green-500/10 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
 archived:
 'bg-n-slate-3 text-n-slate-10 border-n-weak',
 };
 return map[formStatus.value] || map.draft;
});

const statusLabel = computed(() => {
 const map = {
 draft: 'Rascunho',
 published: 'Publicado',
 archived: 'Arquivado',
 };
 return map[formStatus.value] || 'Rascunho';
});

// Provide fields to child components
provide('formFields', fields);
provide('selectedFieldIndex', selectedFieldIndex);

function generateFieldId() {
 return `field_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
}

const TRACKING_FIELDS = [
 // UTM params
 { name: 'utm_source', label: 'UTM Source', tracking_param: 'utm_source' },
 { name: 'utm_medium', label: 'UTM Medium', tracking_param: 'utm_medium' },
 { name: 'utm_campaign', label: 'UTM Campaign', tracking_param: 'utm_campaign' },
 { name: 'utm_term', label: 'UTM Term', tracking_param: 'utm_term' },
 { name: 'utm_content', label: 'UTM Content', tracking_param: 'utm_content' },
 // Page context
 { name: 'referrer', label: 'Referrer', tracking_param: 'referrer' },
 { name: 'page_url', label: 'Page URL', tracking_param: 'page_url' },
 { name: 'landing_page', label: 'Landing Page', tracking_param: 'landing_page' },
 // Click IDs - Google
 { name: 'gclid', label: 'Google Ads (gclid)', tracking_param: 'gclid' },
 { name: 'gbraid', label: 'Google Ads (gbraid)', tracking_param: 'gbraid' },
 { name: 'wbraid', label: 'Google Ads (wbraid)', tracking_param: 'wbraid' },
 // Click IDs - Meta
 { name: 'fbclid', label: 'Facebook (fbclid)', tracking_param: 'fbclid' },
 { name: 'fbc', label: 'Facebook Conv. (fbc)', tracking_param: 'fbc' },
 { name: 'fbp', label: 'Facebook Pixel (fbp)', tracking_param: 'fbp' },
 // Click IDs - TikTok
 { name: 'ttclid', label: 'TikTok (ttclid)', tracking_param: 'ttclid' },
 // Click IDs - Microsoft
 { name: 'msclkid', label: 'Microsoft Ads (msclkid)', tracking_param: 'msclkid' },
 // Click IDs - LinkedIn
 { name: 'li_fat_id', label: 'LinkedIn (li_fat_id)', tracking_param: 'li_fat_id' },
 // Click IDs - Twitter/X
 { name: 'twclid', label: 'Twitter/X (twclid)', tracking_param: 'twclid' },
 // Click IDs - Pinterest
 { name: 'epik', label: 'Pinterest (epik)', tracking_param: 'epik' },
 // Click IDs - Snapchat
 { name: 'ScCid', label: 'Snapchat (ScCid)', tracking_param: 'ScCid' },
 // Click IDs - Reddit
 { name: 'rdt_cid', label: 'Reddit (rdt_cid)', tracking_param: 'rdt_cid' },
 // Click IDs - Snapchat (alt)
 { name: 'sck', label: 'Snapchat (sck)', tracking_param: 'sck' },
];

function addField(fieldType, { silent = false } = {}) {
 if (fieldType === 'tracking_bundle') {
 // Check if tracking fields already exist
 const hasTracking = fields.value.some(
 f => f.type === 'hidden' && f.is_tracking
 );
 if (hasTracking) {
 if (!silent) useAlert('Campos de tracking ja adicionados neste formulario.');
 return;
 }
 TRACKING_FIELDS.forEach(tf => {
 fields.value.push({
 id: generateFieldId(),
 type: 'hidden',
 label: tf.label,
 placeholder: '',
 name: tf.name,
 required: false,
 maps_to: '',
 width: 'full',
 help_text: '',
 validations: {},
 conditions: [],
 defaultValue: '',
 is_tracking: true,
 tracking_param: tf.tracking_param,
 });
 });
 if (!silent) useAlert(`${TRACKING_FIELDS.length} campos de tracking adicionados!`);
 return;
 }
 const newField = createDefaultField(fieldType);
 fields.value.push(newField);
 selectedFieldIndex.value = fields.value.length - 1;
 rightTab.value = 'field';
}

function createDefaultField(type) {
 const defaults = {
 text: {
 label: 'Campo de Texto',
 placeholder: 'Digite aqui...',
 type: 'text',
 },
 email: {
 label: 'Email',
 placeholder: 'nome@exemplo.com',
 type: 'email',
 },
 phone: {
 label: 'Telefone',
 placeholder: '(00) 00000-0000',
 type: 'phone',
 },
 number: {
 label: 'Numero',
 placeholder: '0',
 type: 'number',
 },
 date: {
 label: 'Data',
 placeholder: 'dd/mm/aaaa',
 type: 'date',
 },
 select: {
 label: 'Selecionar',
 placeholder: 'Escolha uma opcao',
 type: 'select',
 options: ['Opcao 1', 'Opcao 2', 'Opcao 3'],
 },
 multi_select: {
 label: 'Selecao Multipla',
 placeholder: 'Escolha opcoes',
 type: 'multi_select',
 options: ['Opcao 1', 'Opcao 2', 'Opcao 3'],
 },
 radio: {
 label: 'Escolha',
 placeholder: '',
 type: 'radio',
 options: ['Opcao 1', 'Opcao 2', 'Opcao 3'],
 },
 checkbox: {
 label: 'Checkbox',
 placeholder: '',
 type: 'checkbox',
 options: ['Opcao 1', 'Opcao 2'],
 },
 textarea: {
 label: 'Texto longo',
 placeholder: 'Escreva sua mensagem...',
 type: 'textarea',
 },
 file: {
 label: 'Upload de Arquivo',
 placeholder: 'Selecione um arquivo',
 type: 'file',
 accept: '',
 maxSize: 10,
 },
 heading: {
 label: 'Título da Seção',
 placeholder: '',
 type: 'heading',
 content: 'Título da Seção',
 },
 paragraph: {
 label: 'Paragrafo',
 placeholder: '',
 type: 'paragraph',
 content: 'Texto descritivo do paragrafo.',
 },
 divider: {
 label: 'Divisor',
 placeholder: '',
 type: 'divider',
 },
 rating: {
 label: 'Avaliação',
 placeholder: '',
 type: 'rating',
 maxRating: 5,
 },
 scale: {
 label: 'Escala',
 placeholder: '',
 type: 'scale',
 minValue: 0,
 maxValue: 10,
 minLabel: 'Nada provavel',
 maxLabel: 'Muito provavel',
 },
 hidden: {
 label: 'Campo Oculto',
 placeholder: '',
 type: 'hidden',
 defaultValue: '',
 },
 cpf: {
 label: 'CPF',
 placeholder: '000.000.000-00',
 type: 'cpf',
 },
 cnpj: {
 label: 'CNPJ',
 placeholder: '00.000.000/0000-00',
 type: 'cnpj',
 },
 cep: {
 label: 'CEP',
 placeholder: '00000-000',
 type: 'cep',
 },
 currency: {
 label: 'Valor (R$)',
 placeholder: 'R$ 0,00',
 type: 'currency',
 },
 };

 // Auto-map known field types to contact fields
 const autoMapsTo = {
 email: 'contact.email',
 phone: 'contact.phone_number',
 };

 const base = defaults[type] || defaults.text;
 return {
 id: generateFieldId(),
 name: '',
 required: false,
 maps_to: autoMapsTo[type] || '',
 width: 'full',
 help_text: '',
 validations: {},
 conditions: [],
 ...base,
 };
}

function onSelectField(index) {
 selectedFieldIndex.value = index;
 rightTab.value = 'field';
}

function onRemoveField(index) {
 fields.value.splice(index, 1);
 if (selectedFieldIndex.value === index) {
 selectedFieldIndex.value = null;
 } else if (
 selectedFieldIndex.value !== null &&
 selectedFieldIndex.value > index
 ) {
 selectedFieldIndex.value -= 1;
 }
}

function onDuplicateField(index) {
 const original = fields.value[index];
 const copy = {
 ...JSON.parse(JSON.stringify(original)),
 id: generateFieldId(),
 label: `${original.label} (copia)`,
 };
 fields.value.splice(index + 1, 0, copy);
}

function onReorderFields(newFields) {
 fields.value = newFields;
}

function onUpdateField(updatedField) {
 if (selectedFieldIndex.value === null) return;
 fields.value[selectedFieldIndex.value] = {
 ...fields.value[selectedFieldIndex.value],
 ...updatedField,
 };
}

function slugify(text) {
 return (text || '')
 .toLowerCase()
 .normalize('NFD')
 .replace(/[\u0300-\u036f]/g, '')
 .replace(/[^a-z0-9]+/g, '_')
 .replace(/^_|_$/g, '')
 .substring(0, 60);
}

function buildFormFieldsAttributes() {
 // Current field IDs (only numeric = from DB)
 const currentDbIds = fields.value
 .filter(f => typeof f.dbId === 'number')
 .map(f => f.dbId);

 // Fields that were deleted (existed in DB, not present anymore)
 const deletedIds = originalFieldDbIds.value.filter(
 id => !currentDbIds.includes(id)
 );

 const attrs = fields.value.map((field, index) => {
 const attr = {
 field_type: field.type,
 label: field.label || '',
 placeholder: field.placeholder || '',
 name: field.name || slugify(field.label) || `field_${index}`,
 required: !!field.required,
 position: index,
 maps_to: field.maps_to || '',
 validations: field.validations || {},
 options: field.options || [],
 conditional_rules: field.conditions || [],
 settings: {
 width: field.width,
 help_text: field.help_text,
 content: field.content,
 accept: field.accept,
 max_size: field.maxSize,
 max_rating: field.maxRating,
 min_value: field.minValue,
 max_value: field.maxValue,
 min_label: field.minLabel,
 max_label: field.maxLabel,
 default_value: field.defaultValue,
 is_tracking: field.is_tracking || false,
 tracking_param: field.tracking_param || '',
 },
 };
 // Include DB id for existing fields (so Rails updates instead of creating)
 if (typeof field.dbId === 'number') {
 attr.id = field.dbId;
 }
 if (field.form_step_id) {
 attr.form_step_id = field.form_step_id;
 }
 return attr;
 });

 // Append _destroy entries for removed fields
 deletedIds.forEach(id => {
 attrs.push({ id, _destroy: true });
 });

 return attrs;
}

function buildFormStepsAttributes() {
 if (formType.value !== 'multi_step') return [];

 const currentDbIds = steps.value
 .filter(s => typeof s.dbId === 'number')
 .map(s => s.dbId);

 const deletedIds = originalStepDbIds.value.filter(
 id => !currentDbIds.includes(id)
 );

 const attrs = steps.value.map((step, index) => {
 const attr = {
 title: step.name || step.title || `Etapa ${index + 1}`,
 description: step.description || '',
 position: index,
 };
 if (typeof step.dbId === 'number') {
 attr.id = step.dbId;
 }
 return attr;
 });

 deletedIds.forEach(id => {
 attrs.push({ id, _destroy: true });
 });

 return attrs;
}

function buildFormPayload() {
 return {
 name: formName.value,
 description: formDescription.value,
 form_type: formType.value,
 status: formStatus.value,
 // Top-level settings (backend expects these as columns, not inside settings)
 submit_action: submitAction.value,
 redirect_url: submitRedirectUrl.value,
 thank_you_message: submitThankYouMessage.value,
 inbox_id: submitInboxId.value,
 conversion_identifier: conversionIdentifier.value,
 also_create_conversation: alsoCreateConversation.value,
 redirect_rules: redirectRules.value,
 exit_rules: exitRules.value,
 // JSONB settings for UI-only preferences
 settings: {
 primary_color: primaryColor.value,
 theme: formTheme.value,
 show_branding: showBranding.value,
 },
 // Nested attributes for Rails accepts_nested_attributes_for
 form_fields_attributes: buildFormFieldsAttributes(),
 form_steps_attributes: buildFormStepsAttributes(),
 };
}

async function onSave() {
 const payload = buildFormPayload();
 try {
 let savedForm;
 if (isEditing.value) {
 savedForm = await store.dispatch('forms/update', {
 id: Number(formId.value),
 ...payload,
 });
 } else {
 savedForm = await store.dispatch('forms/create', payload);
 if (savedForm?.id) {
 router.replace(
 accountScopedRoute('forms_edit', { formId: savedForm.id })
 );
 }
 }
 if (savedForm) loadForm(savedForm);
 useAlert('Formulario salvo com sucesso!');
 } catch (error) {
 const detail = error?.message ? `: ${error.message}` : '';
 useAlert(`Erro ao salvar formulario${detail}`);
 }
}

async function onPublish() {
 if (!isEditing.value) {
 const payload = buildFormPayload();
 payload.status = 'published';
 try {
 const form = await store.dispatch('forms/create', payload);
 if (form?.id) {
 router.replace(
 accountScopedRoute('forms_edit', { formId: form.id })
 );
 loadForm(form);
 }
 useAlert('Formulario publicado!');
 } catch (error) {
 const detail = error?.message ? `: ${error.message}` : '';
 useAlert(`Erro ao publicar formulario${detail}`);
 }
 return;
 }
 try {
 await onSave();
 const published = await store.dispatch('forms/publish', Number(formId.value));
 formStatus.value = 'published';
 embedToken.value = published?.embed_token || embedToken.value || '';
 formSlug.value = published?.slug || formSlug.value || '';
 useAlert('Formulario publicado!');
 } catch (error) {
 const detail = error?.message ? `: ${error.message}` : '';
 useAlert(`Erro ao publicar formulario${detail}`);
 }
}

function onBack() {
 router.push(accountScopedRoute('forms_index'));
}

function loadForm(form) {
 formName.value = form.name || 'Novo Formulario';
 formDescription.value = form.description || '';
 formType.value = form.form_type || 'standard';
 formStatus.value = form.status || 'draft';

 // Backend returns form_fields (not fields), each with field_type (not type)
 const backendFields = Array.isArray(form.form_fields)
 ? form.form_fields
 : [];
 fields.value = backendFields.map(f => {
 const fieldSettings = f.settings || {};
 return {
 id: f.id ? `db_${f.id}` : generateFieldId(),
 dbId: f.id || null,
 type: f.field_type,
 label: f.label || '',
 placeholder: f.placeholder || '',
 name: f.name || '',
 required: !!f.required,
 maps_to: f.maps_to || '',
 form_step_id: f.form_step_id || null,
 validations: f.validations || {},
 options: f.options || [],
 conditions: f.conditional_rules || [],
 // Flatten settings into field props for the UI
 width: fieldSettings.width || 'full',
 help_text: fieldSettings.help_text || '',
 content: fieldSettings.content || '',
 accept: fieldSettings.accept || '',
 maxSize: fieldSettings.max_size || 10,
 maxRating: fieldSettings.max_rating || 5,
 minValue: fieldSettings.min_value || 0,
 maxValue: fieldSettings.max_value || 10,
 minLabel: fieldSettings.min_label || '',
 maxLabel: fieldSettings.max_label || '',
 defaultValue: fieldSettings.default_value || '',
 is_tracking: fieldSettings.is_tracking || false,
 tracking_param: fieldSettings.tracking_param || '',
 };
 });
 // Track original DB IDs for deletion detection
 originalFieldDbIds.value = backendFields
 .filter(f => f.id)
 .map(f => f.id);

 // Backend returns form_steps (not steps), with title (not name)
 const backendSteps = Array.isArray(form.form_steps)
 ? form.form_steps
 : [];
 if (backendSteps.length > 0) {
 steps.value = backendSteps.map(s => ({
 id: s.id ? `db_${s.id}` : `step_${Date.now()}`,
 dbId: s.id || null,
 name: s.title || '',
 description: s.description || '',
 }));
 originalStepDbIds.value = backendSteps
 .filter(s => s.id)
 .map(s => s.id);
 } else {
 steps.value = [{ id: 'step_1', name: 'Etapa 1', fields: [] }];
 originalStepDbIds.value = [];
 }

 alsoCreateConversation.value = form.also_create_conversation || false;
 redirectRules.value = Array.isArray(form.redirect_rules)
 ? form.redirect_rules
 : [];
 exitRules.value = Array.isArray(form.exit_rules)
 ? form.exit_rules
 : [];

 // Top-level settings from backend columns
 submitAction.value = form.submit_action || 'message';
 submitRedirectUrl.value = form.redirect_url || '';
 submitThankYouMessage.value =
 form.thank_you_message || 'Obrigado pelo envio!';
 submitInboxId.value = form.inbox_id || null;
 conversionIdentifier.value = form.conversion_identifier || '';

 // Embed token and slug from backend
 embedToken.value = form.embed_token || '';
 formSlug.value = form.slug || '';

 // JSONB settings for UI prefs
 const settings = form.settings || {};
 primaryColor.value = settings.primary_color || BRAND_PRIMARY;
 formTheme.value = settings.theme || 'light';
 showBranding.value =
 settings.show_branding !== undefined ? settings.show_branding : true;
}

onMounted(async () => {
 if (isEditing.value) {
 try {
 const form = await store.dispatch(
 'forms/show',
 Number(formId.value)
 );
 if (form) loadForm(form);
 } catch (error) {
 // Could not load
 }
 } else {
 // New form: add tracking fields by default (silent = no toast)
 addField('tracking_bundle', { silent: true });
 }
});

// Watch for route changes (if navigating between forms)
watch(
 () => route.params.formId,
 async newId => {
 if (newId) {
 try {
 const form = await store.dispatch('forms/show', Number(newId));
 if (form) loadForm(form);
 } catch (error) {
 // Could not load
 }
 }
 }
);
</script>

<template>
 <div class="flex flex-col h-full w-full bg-n-slate-2">
 <!-- Top Bar -->
 <div
 class="flex items-center justify-between px-4 py-3 bg-n-solid-1 border-b border-n-weak shrink-0"
 >
 <div class="flex items-center gap-3">
 <button
 class="flex items-center justify-center size-8 rounded-lg text-n-slate-10 hover:text-n-slate-11 hover:bg-n-alpha-1 dark:text-n-slate-9 transition-colors"
 @click="onBack"
 >
 <span class="i-lucide-arrow-left size-4" />
 </button>

 <input
 v-model="formName"
 type="text"
 class="reset-base text-base font-semibold text-n-slate-12 bg-transparent border-none outline-none focus:ring-0 w-64"
 placeholder="Nome do formulario"
 />

 <span
 class="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border"
 :class="statusBadgeClass"
 >
 {{ statusLabel }}
 </span>
 </div>

 <div class="flex items-center gap-2">
 <button
 v-if="isEditing"
 class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-n-slate-11 bg-n-solid-1 border border-n-weak rounded-lg hover:bg-n-alpha-1 transition-colors"
 @click="showEmbedModal = true"
 >
 <span class="i-lucide-code size-4" />
 Embed
 </button>

 <button
 class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-n-slate-11 bg-n-solid-1 border border-n-weak rounded-lg hover:bg-n-alpha-1 transition-colors"
 @click="onSave"
 :disabled="isSaving"
 >
 <span class="i-lucide-save size-4" />
 Salvar
 </button>

 <button
 class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 transition-colors shadow-sm"
 @click="onPublish"
 :disabled="isSaving"
 >
 <span class="i-lucide-globe size-4" />
 Publicar
 </button>
 </div>
 </div>

 <!-- Editor Body: 3-panel layout -->
 <div class="flex flex-1 min-h-0">
 <!-- Left Sidebar: Field Palette -->
 <div
 class="w-64 shrink-0 bg-n-solid-1 border-r border-n-weak overflow-y-auto"
 >
 <FieldPalette @add-field="addField" />

 <!-- Step Manager (for multi_step forms) -->
 <div
 v-if="formType === 'multi_step'"
 class="border-t border-n-weak"
 >
 <StepManager
 v-model:steps="steps"
 :fields="fields"
 />
 </div>
 </div>

 <!-- Center: Canvas -->
 <div class="flex-1 overflow-y-auto p-6">
 <FieldCanvas
 :fields="fields"
 :selected-index="selectedFieldIndex"
 :form-name="formName"
 :primary-color="primaryColor"
 @select="onSelectField"
 @remove="onRemoveField"
 @duplicate="onDuplicateField"
 @reorder="onReorderFields"
 @add-field="addField"
 />
 </div>

 <!-- Right Sidebar: Properties / Settings -->
 <div
 class="w-80 shrink-0 bg-n-solid-1 border-l border-n-weak flex flex-col overflow-hidden"
 >
 <!-- Right panel tabs -->
 <div
 class="flex border-b border-n-weak shrink-0"
 >
 <button
 class="flex-1 px-3 py-2.5 text-xs font-medium text-center transition-colors relative"
 :class="
 rightTab === 'field'
 ? 'text-woot-600 dark:text-woot-400'
 : 'text-n-slate-10 hover:text-n-slate-12'
 "
 @click="rightTab = 'field'"
 >
 Campo
 <span
 v-if="rightTab === 'field'"
 class="absolute bottom-0 left-0 right-0 h-0.5 bg-woot-500"
 />
 </button>
 <button
 class="flex-1 px-3 py-2.5 text-xs font-medium text-center transition-colors relative"
 :class="
 rightTab === 'settings'
 ? 'text-woot-600 dark:text-woot-400'
 : 'text-n-slate-10 hover:text-n-slate-12'
 "
 @click="rightTab = 'settings'"
 >
 Configurações
 <span
 v-if="rightTab === 'settings'"
 class="absolute bottom-0 left-0 right-0 h-0.5 bg-woot-500"
 />
 </button>
 </div>

 <div class="flex-1 overflow-y-auto">
 <FieldProperties
 v-if="rightTab === 'field'"
 :field="selectedField"
 :fields="fields"
 @update="onUpdateField"
 />
 <FormSettings
 v-else
 v-model:name="formName"
 v-model:description="formDescription"
 v-model:form-type="formType"
 v-model:submit-action="submitAction"
 v-model:redirect-url="submitRedirectUrl"
 v-model:thank-you-message="submitThankYouMessage"
 v-model:inbox-id="submitInboxId"
 v-model:conversion-identifier="conversionIdentifier"
 v-model:primary-color="primaryColor"
 v-model:form-theme="formTheme"
 v-model:show-branding="showBranding"
 v-model:also-create-conversation="alsoCreateConversation"
 v-model:redirect-rules="redirectRules"
 v-model:exit-rules="exitRules"
 :form-fields="fields"
 />
 </div>
 </div>
 </div>

 <!-- Embed Code Modal -->
 <EmbedCodeModal
 v-if="showEmbedModal"
 :form-id="formId"
 :embed-token="embedToken"
 :form="{ name: formName, embed_token: embedToken, slug: formSlug, conversion_identifier: conversionIdentifier }"
 @close="showEmbedModal = false"
 />
 </div>
</template>
